import React, { useEffect, useMemo, useState } from "react";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import TableHeader from "./TableHeader";
import TableRow from "./TableRow";
import { Pagination, Stack } from "@mui/material";
import { Link } from "react-router-dom";

export const RowLink = styled(Link)(() => ({
  cursor: "pointer",

  "&:hover *": {
    fontWeight: "bolder",
    cursor: "pointer",
    userSelect: "none",
  },
}));

const useSortableData = (items, config) => {
  const [sortConfig, setSortConfig] = React.useState(config);
  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig !== null) {
      if (sortConfig.key?.includes("DATE")) {
        sortableItems.sort((a, b) => {
          if (new Date(a[sortConfig.key]) <= new Date(b[sortConfig.key])) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (new Date(a[sortConfig.key]) > new Date(b[sortConfig.key])) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        });
      } else {
        sortableItems.sort((a, b) => {
          if (a[sortConfig.key] === null && b[sortConfig.key] === null) {
            return 0;
          }
          if (a[sortConfig.key] === null && b[sortConfig.key] !== null) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          if (a[sortConfig.key] !== null && b[sortConfig.key] === null) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }

          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        });
      }
    }

    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = "descending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "descending"
    ) {
      direction = "ascending";
    }
    setSortConfig({ key, direction });
  };
  return { sortedItems, requestSort, sortConfig };
};

const Table = ({
  tab,
  keys,
  items,
  translation,
  config,
  hasDelete,
  handelRowUpdateClick,
  handelRowDeleteClick,
  handelRowCancel,
  withIndex,
  noPagination,
  editable,
  onClick,
  headerHeight = 0,
  isRtl,
  handelRowClick = () => {},
  linkTo = () => {},
  clickable = true,
  style,
  fromTask = false,
}) => {
  const { t } = useTranslation(translation);
  const theme = useTheme();

  const { sortedItems, requestSort, sortConfig } = useSortableData(items, {
    ...config,
  });

  const [page, setPage] = useState(1);
  const rownum = Math.floor(
    (document.body.offsetHeight - 400 - headerHeight) / 50
  );

  useEffect(() => {
    setPage(1);
  }, [items, sortedItems]);

  const handleChange = (event, value) => {
    setPage(value);
  };

  return (
    <div>
      {sortedItems?.length === 0 && (
        <div
          style={{
            justifyContent: "center",
            color: theme.color.text,
            fontSize: "21px",
            margin: "10px 50px",
          }}
        >
          {t("no_items")}
        </div>
      )}
      {sortedItems?.length > 0 && (
        <div>
          {!noPagination && sortedItems.length > rownum && (
            <Stack
              spacing={2}
              style={{
                alignItems: "end",
                marginTop: !fromTask ? 20 : "unset",
                marginBottom: 20,
                marginInlineEnd: !fromTask ? 42 : "unset",
              }}
            >
              <Pagination
                count={
                  Math.floor(sortedItems.length % rownum) !== 0
                    ? Math.floor(sortedItems.length / rownum) + 1
                    : Math.floor(sortedItems.length / rownum) + 0
                }
                sx={{
                  direction: "ltr",
                }}
                shape="rounded"
                page={page}
                onChange={handleChange}
              />
            </Stack>
          )}
          <TableHeader
            keys={keys}
            items={items}
            handelSort={requestSort}
            translation={translation}
            config={sortConfig}
            hasDelete={hasDelete}
            withIndex={withIndex}
            editable={editable}
            fromTask={fromTask}
          />
          <div
            style={{
              overflowY: noPagination === true ? "auto" : "none",
              resize: "vertical",
              height: noPagination === true ? `60vh` : "auto",
              ...style,
            }}
          >
            {(noPagination === true
              ? sortedItems
              : sortedItems.slice((page - 1) * rownum, page * rownum)
            ).map((item, index) => {
              const to = linkTo(item);

              return (
                <div key={index}>
                  {clickable ? (
                    <RowLink
                      key={index}
                      to={{
                        ...to,
                      }}
                    >
                      <TableRow
                        key={index}
                        keys={keys}
                        index={index + (page - 1) * rownum}
                        tab={tab}
                        row={item}
                        isRtl={isRtl}
                        hasDelete={hasDelete}
                        handelRowClick={handelRowClick}
                        handelRowUpdateClick={handelRowUpdateClick}
                        handelRowDeleteClick={handelRowDeleteClick}
                        handelRowCancel={handelRowCancel}
                        withIndex={withIndex}
                        onClick={onClick}
                        editable={editable}
                      ></TableRow>
                    </RowLink>
                  ) : (
                    <TableRow
                      key={index}
                      keys={keys}
                      index={index + (page - 1) * rownum}
                      tab={tab}
                      row={item}
                      hasDelete={hasDelete}
                      handelRowUpdateClick={handelRowUpdateClick}
                      handelRowDeleteClick={handelRowDeleteClick}
                      handelRowCancel={handelRowCancel}
                      withIndex={withIndex}
                      editable={editable}
                    ></TableRow>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default inject()(observer(Table));
